<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    Btn,
    InputText,
    SvgIcon,
    ICON_TYPE,
} from '@jennifersoft/vue-components-v2';
import PasswordInput from '../../common/PasswordInput.vue';
import Alert from '../../common/Alert.vue';

const props = defineProps<{
    signUpStatus?: { code: string | null; message: string } | null;
}>();

const emit = defineEmits<{
    (e: 'submit', payload: any): void;
    (e: 'hide'): void;
}>();

const { t } = useI18n();

const id = ref('');
const name = ref('');
const password = ref('');
const company = ref('');
const dept = ref('');
const jobTitle = ref('');
const cellphone = ref('');
const email = ref('');
const etc = ref('');

// 선택 입력 사항: 기본 접힌 상태
const optionalExpanded = ref(false);
const toggleOptional = () => {
    optionalExpanded.value = !optionalExpanded.value;
};

const SIGN_UP_STATUS_MAP: Record<string, string> = {
    M0329: 'INVALID_USER',
    M0103: 'REQUIRED_FIELD_MISSING',
    M0118: 'INVALID_ID',
    M0101: 'ID_ALREADY_EXISTS',
    M0104: 'INVALID_PASSWORD',
    M0107: 'SIGN_UP_SUCCESS',
    // Password Policy
    M0642: 'PW_SHORT',
    M0643: 'PW_LESS_LOWERCASE',
    M0644: 'PW_LESS_UPPERCASE',
    M0645: 'PW_LESS_DIGIT',
    M0646: 'PW_LESS_SPECIAL',
    M0733: 'PW_LESS_SAME_COUNT',
};

const isIDInvalid = computed(() => {
    if (!props.signUpStatus?.code) return false;
    const status = SIGN_UP_STATUS_MAP[props.signUpStatus.code];
    return ['INVALID_ID', 'ID_ALREADY_EXISTS', 'INVALID_USER'].includes(status);
});

const isPasswordInvalid = computed(() => {
    if (!props.signUpStatus?.code) return false;
    const status = SIGN_UP_STATUS_MAP[props.signUpStatus.code];
    return [
        'PW_SHORT',
        'PW_LESS_LOWERCASE',
        'PW_LESS_UPPERCASE',
        'PW_LESS_DIGIT',
        'PW_LESS_SPECIAL',
        'PW_LESS_SAME_COUNT',
        'INVALID_PASSWORD',
    ].includes(status);
});

const isPhoneInvalid = computed(() => {
    if (!cellphone.value) return false;
    const regx = /^[0-9\+\-\s]+$/i;
    return !regx.test(cellphone.value);
});

const isEmailInvalid = computed(() => {
    if (!email.value) return false;
    const regx = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/i;
    return !regx.test(email.value);
});

const allRequired = computed(() => {
    return (
        id.value.length > 0 &&
        name.value.length > 0 &&
        password.value.length > 0
    );
});

const message = computed(() => props.signUpStatus?.message);

const submitSignForm = () => {
    if (isPhoneInvalid.value || isEmailInvalid.value) return;

    emit('submit', {
        id: id.value,
        name: name.value,
        password: password.value,
        company: company.value,
        dept: dept.value,
        jobTitle: jobTitle.value,
        cellphone: cellphone.value,
        email: email.value,
        etc: etc.value,
    });
};
</script>

<template>
    <div class="sign-up">
        <div class="title">{{ t('M0497') }}</div>
        <div class="sign-in-msg">
            <span style="margin-right: 8px">{{ t('M0639') }}</span>
            <span class="link" @click="emit('hide')">
                {{ t('ui.button.login') }}
            </span>
        </div>
        <form class="form-group" @submit.prevent>
            <div class="sub-title required">
                <SvgIcon
                    :icon="ICON_TYPE.asterisk"
                    :width="16"
                    :height="16"
                    class="asterisk"
                />
                <span>{{ t('ui.label.requiredFields') }}</span>
            </div>
            <div class="field-alert-group">
                <InputText
                    v-model="id"
                    name="id"
                    autocomplete="username"
                    :placeholder="t('ui.label.userId')"
                    :invalid="isIDInvalid"
                    size="large"
                />
                <Alert
                    v-if="isIDInvalid"
                    :message="message || ''"
                    type="danger"
                    fade-down
                />
            </div>
            <InputText
                v-model="name"
                name="name"
                autocomplete="off"
                :placeholder="t('ui.label.userName')"
                size="large"
            />
            <div class="field-alert-group">
                <PasswordInput
                    v-model="password"
                    name="password"
                    autocomplete="new-password"
                    :placeholder="t('ui.label.password')"
                    :invalid="isPasswordInvalid"
                    size="large"
                />
                <Alert
                    v-if="isPasswordInvalid"
                    :message="message || ''"
                    type="danger"
                    fade-down
                />
            </div>
            <button
                type="button"
                class="sub-title collapsible"
                :class="{ expanded: optionalExpanded }"
                :aria-expanded="optionalExpanded"
                aria-controls="optional-fields"
                @click="toggleOptional"
            >
                <SvgIcon
                    :icon="ICON_TYPE.chevronRight"
                    :width="16"
                    :height="16"
                    class="chevron"
                />
                <span>{{ t('ui.label.optionalFields') }}</span>
            </button>
            <div
                id="optional-fields"
                class="optional-collapse"
                :class="{ expanded: optionalExpanded }"
                :aria-hidden="!optionalExpanded"
                :inert="!optionalExpanded"
            >
                <div class="optional-fields">
                <InputText
                    v-model="company"
                    :placeholder="t('ui.label.company')"
                    size="large"
                />
                <div class="inline">
                    <InputText
                        v-model="dept"
                        :placeholder="t('ui.label.dept')"
                        size="large"
                    />
                    <InputText
                        v-model="jobTitle"
                        :placeholder="t('ui.label.jobTitle')"
                        size="large"
                    />
                </div>
                <div class="field-alert-group">
                    <InputText
                        v-model="cellphone"
                        :invalid="isPhoneInvalid"
                        :placeholder="t('ui.label.cellphone')"
                        size="large"
                    />
                    <Alert
                        v-if="isPhoneInvalid"
                        :message="t('M0300')"
                        type="danger"
                        fade-down
                    />
                </div>
                <div class="field-alert-group">
                    <InputText
                        v-model="email"
                        :invalid="isEmailInvalid"
                        :placeholder="t('ui.label.email')"
                        size="large"
                    />
                    <Alert
                        v-if="isEmailInvalid"
                        :message="t('M0299')"
                        type="danger"
                        fade-down
                    />
                </div>
                <InputText
                    v-model="etc"
                    :placeholder="t('word.etc')"
                    size="large"
                />
                </div>
            </div>
        </form>
        <div class="footer">
            <Btn
                :text="t('ui.button.joinIn')"
                :disabled="!allRequired"
                class="sign-up-btn"
                type="primary-filled"
                size="large"
                @click="submitSignForm"
            />
        </div>
    </div>
</template>

<style lang="scss">
.sign-up {
    display: flex;
    flex-direction: column;
    > .title {
        font-size: 32px;
        font-weight: bold;
        color: var(--gray-900, #222229);
    }
    .form-group {
        display: flex;
        flex-direction: column;
        flex: 1;
        margin: 40px 0;
        gap: 16px;

        > .sub-title {
            font-size: 14px;
            font-weight: bold;
            color: var(--gray-900, #222229);
            &:not(:first-child) {
                margin-top: 24px;
            }

            &.required,
            &.collapsible {
                display: flex;
                align-items: center;
                gap: 4px;
            }

            &.collapsible {
                border: 0;
                background: transparent;
                padding: 0;
                text-align: left;
                font-family: inherit;
                cursor: pointer;
                user-select: none;

                &:focus {
                    outline: none;
                }

                .chevron {
                    transition: transform 0.15s ease;
                }
                &.expanded .chevron {
                    transform: rotate(90deg);
                }
            }
        }

        > .optional-collapse {
            display: grid;
            grid-template-rows: 0fr;
            transition: grid-template-rows 0.2s ease;

            &.expanded {
                grid-template-rows: 1fr;
            }

            > .optional-fields {
                min-height: 0;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                gap: 16px;
            }
        }

        .inline {
            display: flex;
            > :first-child {
                margin-right: 16px;
            }
        }
    }

    .sign-in-msg {
        margin-top: 16px;
        font-size: 16px;
        line-height: 1.5;
        color: var(--gray-600, #75757c);

        .link {
            color: var(--primary-700, #8652ff);
            cursor: pointer;
        }
    }

    .footer {
        display: flex;
        justify-content: flex-end;
        position: relative;
    }
}
</style>
